import * as React from 'react'
import {Suspense, useEffect, useRef, useState} from 'react'
import AliceCarousel from 'react-alice-carousel'
import {Helmet} from 'react-helmet'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import CarouselOutline from '../components/carouselOutline'
import SimpleList from '../components/simpleList'
import Steps from '../components/steps'
import '../css/landing.css'
import '../css/monetization.css'
import IconAppwall from '../svg/iconAppWall'
import IconNativeAds from '../svg/iconNativeAds'
import IconOfferWall from '../svg/iconOfferWall'
import IconPlayableAds from '../svg/iconPlayableAds'
import IconRewardedVideo from '../svg/iconRewardedVideo'
import {goToElementId, tagger, setTitleFont} from '../util/util'

const ReactImageMagnify = React.lazy(() => import('react-image-magnify'))
const MonetizationAnimation = React.lazy(() => import('../lotties/monetization/monetization'))

enum Format {
    Iframe = 'iframe',
    Img = 'img',
    Video = 'video'
}

const Monetization = () => {

    const emptyScreenRef = useRef(null) as any

    const {t, i18n} = useTranslation('monetization')
    const tCommon = useTranslation('common').t

    const controlImgList = [
        {small: 'https://static.persona.ly/images/monetization/monetization-1-small.webp', large: 'https://static.persona.ly/images/monetization/monetization-1.webp'},
        {small: 'https://static.persona.ly/images/monetization/monetization-2-small.webp', large: 'https://static.persona.ly/images/monetization/monetization-2.webp'},
        {small: 'https://static.persona.ly/images/monetization/monetization-3-small.webp', large: 'https://static.persona.ly/images/monetization/monetization-3.webp'}
    ]

    const customizeListBlocks = [
        {
            title: t('Control'),
            text: t('Define ad behavior 2'),
            img: 'https://static.persona.ly/images/monetization/group-25.svg'
        },
        {
            title: t('Measure'),
            text: t('Get in depth conversion'),
            img: 'https://static.persona.ly/images/monetization/group-26.svg'
        },
        {
            title: t('Optimize'),
            text: t('Get actionable insights'),
            img: 'https://static.persona.ly/images/monetization/group-29.svg'
        }
    ]

    const simpleListBlocks = [
        {
            title: t('Custom Campaigns'),
            textList: [t('Hand-picked')],
            img: 'https://static.persona.ly/images/monetization/image-custom.webp'
        },
        {
            title: t('Premium eCPM'),
            textList: [t('Unique market research')],
            img: 'https://static.persona.ly/images/monetization/image-premium.webp'
        },
        {
            title: t('High Engagement'),
            textList: [t('Provide your users with 1')],
            img: 'https://static.persona.ly/images/monetization/image-high.webp'
        }
    ]

    const stepsBlocks = [
        {
            title: t('Integrate'),
            textList: [t('Implement our'), 'SDK / APIs'],
            img: 'https://static.persona.ly/images/monetization/impression-level-features-ilu.svg'
        },
        {
            title: t('Customize & Control'),
            textList: [t('Choose your ad'), t('formats & when to'), t('display them')],
            img: 'https://static.persona.ly/images/monetization/user-centric-features-ilu.svg'
        },
        {
            title: t('Maximize'),
            textList: [t('Increase ARPU and'), t('scale your business')],
            img: 'https://static.persona.ly/images/monetization/engagement-analysis-ilu.svg'
        }
    ]

    const interactiveBlocks = [
        {
            title: t('Offer users bite'),
            points: [
                t('Up to x25 higher'),
                t('Over 30% of users'),
                t('Optimized by measuring'),
                t('HTML5, light weight')
            ],
            img: 'https://static.persona.ly/images/monetization/icon_app_wall_inactive.svg',
            preview: {
                format: Format.Iframe,
                src: 'https://static.persona.ly/images/monetization/cookie_jam.html'
            }
        },
        {
            title: t('Reward your users'),
            points: [
                t('Proven to be favored'),
                t('Ideal for monetizing'),
                t('High eCPM and'),
                t('Relevant videos')
            ],
            img: 'https://static.persona.ly/images/icon_native_ads_inactive.svg',
            preview: {
                format: Format.Video,
                src: 'https://static.persona.ly/images/monetization/rewarded_video.mp4'
            }
        },
        {
            title: t('Display ads that'),
            points: [
                t('25x Higher eCPM'),
                t('2x Higher click rate'),
                t('Avoid negative reactions'),
                t('Avoid ad blindness')
            ],
            img: 'https://static.persona.ly/images/monetization/icon_offer_wall_inactive.svg',
            preview: {
                format: Format.Img,
                src: 'https://static.persona.ly/images/monetization/nativeads.webp'
            }
        },
        {
            title: t('Offer your users'),
            points: [
                t('A great way to'),
                t('Offered applications'),
                t('User initiated'),
                t('Customized to your')
            ],
            img: 'https://static.persona.ly/images/monetization/icon_playable_ads_active.svg',
            preview: {
                format: Format.Img,
                src: 'https://static.persona.ly/images/monetization/appwall.webp'
            }
        },
        {
            title: t('Reward your users 2'),
            points: [
                t('Offer users multiple'),
                t('Significantly increase'),
                t('User initiated'),
                t('Contains unique')
            ],
            img: 'https://static.persona.ly/images/monetization/icon_rewarded_video_inactive.svg',
            preview: {
                format: Format.Img,
                src: 'https://static.persona.ly/images/monetization/offerwall.webp'
            }
        }
    ]

    const interactiveButtons = [
        {
            name: t('PLAYABLE ADS'),
            icon: IconPlayableAds
        },
        {
            name: t('REWARDED VIDEO'),
            icon: IconRewardedVideo
        },
        {
            name: t('NATIVE ADS'),
            icon: IconNativeAds
        },
        {
            name: t('APP WALL'),
            icon: IconAppwall
        },
        {
            name: t('OFFER WALL'),
            icon: IconOfferWall
        }
    ]

    const [state, setState] = useState({
        innerWidth: window.innerWidth,
        interactiveButtonIndex: 0,
        interactivePreview: {
            format: Format.Iframe,
            src: 'https://static.persona.ly/images/monetization/cookie_jam.html'
        },
        iFrameHeight: 400
    })

    const [iframe, setIframe] = useState({
        height: 400
    })

    const calcFrameHeight = () => {
        if (emptyScreenRef.current) {
            const height = emptyScreenRef.current.height * 0.7
            setIframe({height: height})
        }
    }

    const scrollToHandler = () => {goToElementId('interactiveExp', true)}

    useEffect(() => {
        window.addEventListener('resize', () => calcFrameHeight())
        setTitleFont(i18n.language)
    }, [])

    const renderInteractiveBlock = () => (
        <div>
            <div className="block-p not-mobile">{interactiveBlocks[state.interactiveButtonIndex].title}</div>
            <ul className="block-c short-li-mobile">
                {interactiveBlocks[state.interactiveButtonIndex].points.map((point, i) =>
                    <li key={'ip-' + i}>{point}</li>)}
            </ul>
        </div>
    )

    const renderInteractiveButtons = () => interactiveButtons.map((button, i) => {
        const isActive = state.interactiveButtonIndex == i
        return (
            <div key={'ib-' + i} className="is-centered">
                {button.icon(isActive, 'not-mobile')}
                <button className={'button-grey outline small ' + (isActive ? 'active' : '')} onClick={() =>
                    setState({
                        ...state,
                        interactiveButtonIndex: i,
                        interactivePreview: interactiveBlocks[i].preview
                    })}>{button.name}</button>
            </div>
        )
    })

    const renderInteractivePreview = () => {
        switch (state.interactivePreview.format) {
            case Format.Iframe:
                return <iframe src={state.interactivePreview.src} frameBorder={0} style={{minHeight: iframe.height}} />
            case Format.Img:
                return <img alt="" src={state.interactivePreview.src} />
            case Format.Video:
                return <video src={state.interactivePreview.src} autoPlay={true} loop={true} playsInline={true} />
        }
    }

    return (
        <div className="monetization">
            <div className="down-scroll not-tablet not-mobile" onClick={scrollToHandler}>
                <p className="down-scroll-text">SCROLL</p>
                <span className="down-scroll-animation down-scroll-animation-red"> </span>
            </div>
            <Helmet>
                <title>Monetize your applications with interactive ads and increase your ARPU</title>
                <meta name="title" content="Monetize your applications with interactive ads and increase your ARPU" />
                <meta name="description" content="Persona.ly offers a programmatic & fully transparent service, from playable and dynamic creative generation, to driving scale and providing actionable marketing insights" />
                <meta name="keywords" content="Mobile Monetization, Playable Ads, Interactive Ads, Dynamic Ads, Performance-based Campaigns, Native Ads,Rewarded Video, CPE Campaigns,CPI Traffic,CPI Offers" />
            </Helmet>
            <div className="main-content">
                <div>
                    <div className="title muli not-desktop not-mobile">{tagger(t('exceptional'), 'red-text')}</div>
                    <div className="monetization-c">
                        <div className="tablet monetization-description">
                            <div className="title muli not-tablet">{tagger(t('exceptional'), 'red-text')}</div>
                            <div className="block-p">{t('Achieved by using')}</div>
                            <div className="block-c">{t('Provides a variety')}</div>
                            <div className="m-two-buttons" style={{marginTop: '2rem'}}>
                                <Link to="sign_up">
                                    <button className="button-red large m-button-mr">
                                        {tCommon('Integrate Now')}<img alt="" src="https://static.persona.ly/images/arrow.svg" />
                                    </button>
                                </Link>
                                <Link to="about#contact">
                                    <button className="button-grey large">{tCommon('Contact Us')}</button>
                                </Link>
                            </div>
                            <div className="block-c not-tablet not-mobile" style={{marginTop: '4rem'}}>
                                {t('Supported platforms')}:
                            </div>
                            <img alt="" className="not-tablet not-mobile" src="https://static.persona.ly/images/group-64.svg" style={{marginTop: 8}} />
                        </div>
                        <div className="lottie-monetization-container tablet">
                            <Suspense fallback="">
                                <MonetizationAnimation />
                            </Suspense>
                        </div>
                    </div>
                    <div className="block-c supported not-desktop" style={{marginTop: '4rem'}}>
                        {t('Supported platforms')}:
                    </div>
                    <img alt="" className="not-desktop" src="https://static.persona.ly/images/group-64.svg" style={{marginTop: 8}} />
                </div>
                <div id="interactiveExp" className="interactive-block">
                    <div>
                        <div className="title muli mt-t">{tagger(t('interactive'), 'red-text')}</div>
                        <div className="block-p">{t('Maximized eCPM')}</div>
                        <div className="button-block" style={{marginTop: '5%'}}>
                            {renderInteractiveButtons()}
                        </div>
                        <div className="content-block">
                            {renderInteractiveBlock()}
                            <div className="block-preview not-desktop">
                                <img ref={emptyScreenRef} alt="" src="https://static.persona.ly/images/monetization/dynamic-ads-ilu.svg"
                                    onLoad={calcFrameHeight} />
                                <div className="loader-container">
                                    <div className="loader inside" />
                                </div>
                                {renderInteractivePreview()}
                            </div>
                        </div>
                        <Link to="sign_up">
                            <button className="button-red large is-block" style={{marginTop: '2rem'}}>
                                {tCommon('Integrate Now')}<img alt="" src="https://static.persona.ly/images/arrow.svg" />
                            </button>
                        </Link>
                    </div>
                    <div className="block-preview not-mobile not-tablet">
                        <img alt="" src="https://static.persona.ly/images/monetization/dynamic-ads-ilu.svg" />
                        <div className="loader-container">
                            <div className="loader outside" />
                        </div>
                        {renderInteractivePreview()}
                    </div>
                </div>
                <div>
                    <div>
                        <div className="title muli mt-t is-tablet">
                            {t('Lightweight SDK')} / <span className="red-text">{t('Campaigns API')}</span>
                        </div>
                        <div>
                            <div className="title muli mt-t not-tablet">
                                {t('Lightweight SDK')} / <span className="red-text">{t('Campaigns API')}</span>
                            </div>
                            <div className="block-p mt-26">{t('Monetize effectively')}</div>
                            <div className="block-c mt-26">{t('Start monetizing your app')}</div>
                        </div>
                    </div>
                </div>
            </div>
            <Steps blocks={stepsBlocks} />
            <div className="additional-block">
                <div>
                    <div className="block-c supported">{t('Supported platforms')}:</div>
                    <img alt="" src="https://static.persona.ly/images/group-64.svg" style={{marginTop: 8, marginBottom: 32, width: '100%'}} />
                    <SimpleList blocks={simpleListBlocks} />
                </div>
                <div className="additional-api">
                    <img alt="" src="https://static.persona.ly/images/monetization/rtb-icon-01.svg" />
                    <div className="block-p">{t('Gain access to')}</div>
                    <div>
                        <Link to="sign_up">
                            <button className="button-red large">
                                {tCommon('Integrate Now')}<img alt="" src="https://static.persona.ly/images/arrow.svg" />
                            </button>
                        </Link>
                        <Link to="about#contact">
                            <button className="button-grey large">{tCommon('Contact Us')}</button>
                        </Link>
                    </div>
                </div>
            </div>
            <div className="customize-optimize">
                <div className="title muli mt-t">{tagger(t('Customize & Maximize'), 'red-text')}</div>
                <div className="block-p mt-26">{t('Define ad behavior')}</div>
                <div className="block-c mt-26">{t('Our dashboard provides')}</div>
            </div>
            <div className="customize-block">
                <div className="block-left">
                    <CarouselOutline blocks={customizeListBlocks} />
                </div>
                <div className="block-right images-rounded">
                    <AliceCarousel
                        autoHeight={false}
                        buttonsDisabled={true}
                        infinite={false}
                        mouseTrackingEnabled={true}
                        items={controlImgList.map((img, i) => (
                            <Suspense key={i} fallback="">
                                <ReactImageMagnify onDragStart={(e) => e.preventDefault()} {...{
                                    smallImage: {
                                        isFluidWidth: true,
                                        src: img.small
                                    },
                                    largeImage: {
                                        src: img.large,
                                        width: 1200,
                                        height: 800
                                    },
                                    style: {cursor: 'zoom-in'},
                                    enlargedImagePosition: 'over',
                                    isHintEnabled: true,
                                    hintTextMouse: 'Hover to Zoom',
                                    hintTextTouch: 'Long-Touch to Zoom'
                                }} />
                            </Suspense>
                        ))}
                    />
                    <div className="block-c mt-control">{tagger(t('Control, measure'), 'red-text')}</div>
                    <Link to="sign_up">
                        <button className="button-red large">
                            {tCommon('Start Now')} <img alt="" src="https://static.persona.ly/images/arrow.svg" />
                        </button>
                    </Link>
                </div>
            </div>
            <div className="offer-wall-block">
                <div className="block-left">
                    <div className="title muli mt-t">{tagger(t('Offer Wall'), 'red-text')}</div>
                    <div className="block-p mt-26">{t('Monetize loyal')}</div>
                    <div className="block-c mt-26">{t('An offer wall is a great way')}</div>
                    <div className="block-c">{t('Provide your users with')}</div>
                    <img alt="" src="https://static.persona.ly/images/group-14.svg" />
                    <Link to="sign_up">
                        <button className="button-red large">
                            {tCommon('Integrate Now')} <img alt="" src="https://static.persona.ly/images/arrow.svg" />
                        </button>
                    </Link>
                </div>
                <div className="block-right">
                    <img alt="" src="https://static.persona.ly/images/monetization/big-offer-wall.webp" />
                </div>
            </div>
        </div>
    )
}

export default Monetization



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/pages/monetization.tsx